import { create, StateCreator } from 'zustand';
import {
  CourierSlice,
  CourierStatusList,
  ICourierDeliveryStatus,
  ICourierETASlice,
  ICourierLocationStatus,
  IOrderETASlice,
  NextRoutesSlice,
  OrderSlice,
  RoutesSlice,
  ServerTimeSlice,
} from './store-model';
import {
  IOrder,
  IRoute,
  ICourierReturnStatus,
  ICourierSnap,
  IDeliveryStatus,
  ICourierLocation,
  ICourierETA,
  IServerTime,
  IOrderETA,
} from '../models/model-interface';

import { subscribeWithSelector, devtools } from 'zustand/middleware';
import { CourierReturnStatusType, DeliveryStatus, OrderStatus } from '../models/model-enum';
import {
  ACTION_COURIER,
  ACTION_COURIER_STATUS,
  ACTION_ORDER,
  ACTION_ROUTE,
  ACTION_LOCATION,
} from '../models/model-actions';
import { addDeliveryStatusHelper, addDeliveryStatusHelperOrder } from './helpers';
import dayjs from 'dayjs';
import { COURIER_STATUS } from '@src/pages/Couriers/services/api';

const createCourierSlice: StateCreator<
  CourierSlice,
  [['zustand/devtools', never]],
  [],
  CourierSlice
> = (set, get) => ({
  couriers: [],
  isCourierEmpty: () => !!get().couriers.length,
  setCouriersList: (data) =>
    set(
      () => ({
        couriers: data.sort((a, b) => {
          return a?.firstName?.localeCompare(b?.firstName);
        }),
      }),
      false,
      ACTION_COURIER.Set
    ),
  addCourier: (courier) =>
    set(
      (state) => ({
        couriers: [...state.couriers, { ...courier }],
      }),
      false,
      ACTION_COURIER.Add
    ),
  updateCourier: (payload) => {
    const courierIndex = get().couriers.findIndex((c: ICourierSnap) => c.id === payload.id);
    set(
      (state) => {
        const draftCourierState = Object.assign(state.couriers, {});
        draftCourierState[courierIndex] = payload;
        return {
          couriers:
            courierIndex !== -1 ? [...draftCourierState] : [...state.couriers, { ...payload }],
        };
      },
      false,
      ACTION_COURIER.Update
    );
  },
});

type CreateOrderSliceCombined = OrderSlice & CourierStatusList & ICourierDeliveryStatus;

const createOrderSlice: StateCreator<
  CreateOrderSliceCombined,
  [['zustand/devtools', never]],
  [],
  OrderSlice
> = (set, get) => ({
  orders: [],
  isOrderEmpty: null,
  setOrdersList: (data) => {
    /* Mock */
    /*     const dat = {
          hash: '23232939209392030299s0asd90as9',
          customer: {
            id: 'Mock_Customer_Id',
            firstName: 'Mock_Customer_First_Name',
            lastName: 'Recop',
            address: {
              street: 'Lindberg',
              streetNumber: '57',
              city: 'Berlin',
              postcode: '10117',
            },
          },
          payment: {
            status: 'Paid',
          },
          hasAlcohol: false,
          expectedDeliveryTime: '2025-07-09T12:15:38.007Z',
          status: OrderStatus.Unassigned,
          timestamp: 0,
          address: 'Mock_Address',
          city: 'Mock_City',
          postCode: 'Mock_PostCode',
          isPaid: true,
          isContainAlcohol: false,
          customerName: 'Mock_Customer',
          courier: null,
        }
        data.push(dat as any); */
    const draftData = Object.assign(data, {});

    draftData
      .filter(
        (d) =>
          d.courier && (d.status === OrderStatus.InDelivery || d.status === OrderStatus.Assigned)
      )
      .map((s) => {
        const fo = get().deliveryStatuses.find((a) => a.courierId === s.courier.id);
        if (fo) {
          get().updateDeliveryStatusByOrder(s);
        } else {
          get().addDeliveryStatusByOrder(s);
        }
      });
    set(() => ({ orders: data }), false, ACTION_ORDER.Set);
  },
  getUnassignedOrderTotalValue: () =>
    get().orders.filter((o) => o.status === OrderStatus.Unassigned).length,
  addOrder: (order) =>
    set(
      (state) => ({
        orders: [...state.orders, order],
      }),
      false,
      ACTION_ORDER.Add
    ),
  getOrderByHash: (orderHash: string) => {
    return get().orders.find((order: IOrder) => order.hash === orderHash);
  },
  getOrderEDTByUUID: (uuid: string) => {
    const order = get().orders.find((o: IOrder) => o.uuid === uuid);
    return order ? order.expectedDeliveryTime : false;
  },

  updateOrder: (payload) => {
    const orderIndex = get().orders.findIndex((o: IOrder) => o.hash === payload.hash);
    if (payload.courier && payload.status === OrderStatus.InDelivery) {
      //const getActiveOrders = get()?.deliveryStatuses?.find((d) => d.activeOrders?.includes(payload.hash))
      get().updateDeliveryStatusByOrder(payload);
    }
    if (payload.courier && payload.status === OrderStatus.Assigned) {
      const activeCourierStatus = get().deliveryStatuses.find(
        (d) => d.courierId === payload.courier.id
      );
      const getCurrentStatus = activeCourierStatus?.currentDeliveryStatus;
      const findCourierStatus = activeCourierStatus;
      if (getCurrentStatus === DeliveryStatus.Returning) {
        get().updateDeliveryStatusByOrder(payload);
      } else {
        if (findCourierStatus) {
          get().updateDeliveryStatusByOrder(payload);
        } else {
          get().addDeliveryStatusByOrder(payload);
        }
      }
    }
    if (
      payload.status === OrderStatus.Unassigned ||
      (payload.courier && payload.status === OrderStatus.Delivered)
    ) {
      const getActiveOrders = get()?.deliveryStatuses?.find((d) =>
        d.activeOrders?.includes(payload.hash)
      );
      if (getActiveOrders && getActiveOrders.currentDeliveryStatus !== DeliveryStatus.Returning) {
        get().clearCourierActiveOrdersFromStatus(getActiveOrders.courierId, payload.hash);
      }
    }

    set(
      (state) => {
        const draftOrderState = Object.assign(state.orders, {});
        draftOrderState[orderIndex] = payload;
        return {
          orders: orderIndex !== -1 ? [...draftOrderState] : [...state.orders],
        };
      },
      false,
      ACTION_ORDER.Update
    );
  },
});

const createRoutesSlice: StateCreator<
  RoutesSlice,
  [['zustand/devtools', never]],
  [],
  RoutesSlice
> = (set, get) => ({
  routes: [],
  setRoutesList: (data) => set(() => ({ routes: data }), false, ACTION_ROUTE.Set),
  addRoute: (route) =>
    set((state) => ({ routes: [...state.routes, { ...route }] }), false, ACTION_ROUTE.Add),

  updateRoute: (payload) => {
    const isCourierActive = get().routes.findIndex(
      (r: IRoute) => r.courier.id === payload.courier.id
    );

    set(
      (state) => {
        const draftRouteState = Object.assign(state.routes, {});
        draftRouteState[isCourierActive] = payload;
        return {
          routes: isCourierActive !== -1 ? [...draftRouteState] : [...state.routes, { ...payload }],
        };
      },
      false,
      ACTION_ROUTE.Update
    );
  },
});

const createCourierStatusSlice: StateCreator<
  CourierStatusList & ICourierDeliveryStatus,
  [['zustand/devtools', never]],
  [],
  CourierStatusList
> = (set, get) => ({
  statuses: [],
  setCourierStatus: (data) => {
    const draftData = Object.assign(data, {});
    const ids = get().deliveryStatuses.map((d) => d.courierId);
    draftData.map((s) => {
      if (ids.includes(s.id)) {
        get().updateDeliveryStatusByStatuses(s);
      } else {
        get().addDeliveryStatusByStatus(s);
      }
    });
    set(() => ({ statuses: data }), false, ACTION_COURIER_STATUS.Set);
  },
  addCourierStatus: (status) =>
    set(
      (state) => ({
        statuses: [...state.statuses, { ...status }],
      }),
      false,
      ACTION_COURIER_STATUS.Add
    ),
  updateCourierStatus: (payload) => {
    const courierStatusIndex = get().statuses.findIndex(
      (s: ICourierReturnStatus) => s.id === payload.id
    );
    const ids = get().deliveryStatuses.map((d) => d.courierId);
    if (payload.status === CourierReturnStatusType.Returning) {
      if (ids.includes(payload.id)) {
        get().updateDeliveryStatusByStatuses(payload);
      } else {
        get().addDeliveryStatusByStatus(payload);
      }
    }
    if (payload.status === CourierReturnStatusType.Arrived) {
      if (ids.includes(payload.id)) {
        get().clearDeliveryStatusByStatuses(payload);
        //console.log('IDS', ids);
      }
    }
    set(
      (state) => {
        const draftCourierStatus = Object.assign(state.statuses, {});
        draftCourierStatus[courierStatusIndex] = payload;
        return {
          statuses:
            courierStatusIndex !== -1
              ? [...draftCourierStatus]
              : [...state.statuses, { ...payload }],
        };
      },
      false,
      ACTION_COURIER_STATUS.Update
    );
  },
  clearCourierStatus: (payload) => {
    set((state) => {
      const draftCourierStatus = Object.assign(
        state.statuses.filter((s: ICourierReturnStatus) => s.id !== payload),
        {}
      );
      return {
        statuses: [...draftCourierStatus],
      };
    });
  },
});

const createCourierDeliveryStatusSlice: StateCreator<
  ICourierDeliveryStatus,
  [['zustand/devtools', never]],
  [],
  ICourierDeliveryStatus
> = (set, get) => ({
  deliveryStatuses: [],
  addDeliveryStatusByOrder: (data) =>
    set(
      (state) => ({
        ...state,
        deliveryStatuses: addDeliveryStatusHelperOrder(
          state.deliveryStatuses,
          data.courier.id,
          data.status,
          data.hash
        ),
      }),
      false,
      'add/D_STATUS_ORDER'
    ),
  addDeliveryStatusByStatus: (data) =>
    set(
      (state) => ({
        ...state,
        deliveryStatuses: addDeliveryStatusHelper(state.deliveryStatuses, data.id, data.status),
      }),
      false,
      'add/D_STATUS_BY_STATUSES'
    ),
  updateDeliveryStatusByOrder: (data: IOrder) => {
    set(
      (state) => {
        const draftState = Object.assign(state.deliveryStatuses, {});
        const findIndex = state.deliveryStatuses.findIndex((a) => a.courierId === data.courier.id);
        draftState[findIndex] = {
          ...draftState[findIndex],
          currentDeliveryStatus:
            findIndex !== -1 ? data.status : (draftState[findIndex].currentDeliveryStatus as any),
          activeOrders:
            draftState[findIndex].currentDeliveryStatus === DeliveryStatus.Returning
              ? [data.hash]
              : draftState[findIndex]?.activeOrders?.includes(data.hash)
              ? draftState[findIndex]?.activeOrders
              : [...(draftState[findIndex].activeOrders as any), data.hash],
        };
        return {
          ...state,
          deliveryStatuses: [...draftState],
        };
      },
      false,
      'update/D_STATUS_ORDER'
    );
  },
  getDeliveryStatusById: () => {
    return get().deliveryStatuses.map((s: IDeliveryStatus) => s.courierId);
  },
  updateDeliveryStatusByStatuses: (data: ICourierReturnStatus) => {
    set(
      (state) => {
        const draftState = Object.assign(state.deliveryStatuses, {});
        const findIndex = state.deliveryStatuses.findIndex((a) => a.courierId === data.id);
        draftState[findIndex] = {
          ...draftState[findIndex],
          currentDeliveryStatus: data.status as any,
        };
        return {
          ...state,
          deliveryStatuses:
            findIndex !== -1
              ? [...draftState]
              : [
                  {
                    courierId: data.id,
                    currentDeliveryStatus: data.status as any,
                  },
                ],
        };
      },
      false,
      'update/D_STATUS_BY_STATUSES'
    );
  },
  clearCourierActiveOrdersFromStatus: (courierId: string, activeOrderId: string) => {
    set(
      (state) => {
        const draftState = Object.assign(state.deliveryStatuses, {});
        const activeOrders = state.deliveryStatuses
          .map((s) => s)
          .find((d) => d.courierId === courierId)
          ?.activeOrders?.filter((f) => f !== activeOrderId);
        const findIndex = state.deliveryStatuses.findIndex((a) => a.courierId === courierId);
        draftState[findIndex] = {
          ...draftState[findIndex],
          activeOrders: activeOrders,
        };
        /* A logic after the last order is removed  if we dont have any active delivery we should remove the courier from list */
        const drafto = draftState.filter((s) => (s.activeOrders?.length as number) !== 0);
        return {
          deliveryStatuses: findIndex !== -1 ? [...drafto] : [...state.deliveryStatuses],
        };
      },
      false,
      'remove/D_STATUS_UNASSIGN'
    );
  },

  clearDeliveryStatusByStatuses: (data: ICourierReturnStatus) => {
    set((state) => {
      const draftCourierStatus = Object.assign(
        state.deliveryStatuses.filter((s: IDeliveryStatus) => s.courierId !== data.id),
        {}
      );
      return {
        deliveryStatuses: [...draftCourierStatus],
      };
    });
  },
});

const createCourierLocationSlice: StateCreator<
  ICourierLocationStatus & CourierSlice,
  [['zustand/devtools', never]],
  [],
  ICourierLocationStatus
> = (set, get) => ({
  courierLocation: [],
  setCourierLocation: (payload) => {
    set(
      (state) => {
        const isCourierLocationValueExist = get().courierLocation.findIndex(
          (l: ICourierLocation) => l.id === payload.id
        );
        return {
          ...state,
          courierLocation:
            isCourierLocationValueExist !== -1
              ? state.courierLocation.map((st) => ({
                  ...st,
                  location: st.id === payload.id ? payload.location : st.location,
                }))
              : [...state.courierLocation, { ...payload }],
        };
      },
      false,
      ACTION_LOCATION.Update
    );
  },
  getCourierLocation: (activeCourierId: string) =>
    get().courierLocation.find((l: ICourierLocation) => l.id === activeCourierId)?.location,
});

const createCourierEtaSlice: StateCreator<
  ICourierETASlice & ICourierDeliveryStatus,
  [['zustand/devtools', never]],
  [],
  ICourierETASlice
> = (set, get) => ({
  courierEta: [],
  setCourierEta: (payload) => set(() => ({ courierEta: payload }), false, 'set/ETA'),
  getCourierEtaById: (courierId: string) =>
    get().courierEta.find((e: ICourierETA) => e.id === courierId)?.eta,
  updateCourierEta: (payload: ICourierETA) => {
    set(
      (state) => {
        const draftState = Object.assign(state.courierEta, {});
        const findIndex = state.courierEta.findIndex((e) => e.id === payload.id);
        draftState[findIndex] = {
          ...draftState[findIndex],
          eta: payload.eta,
        };
        return {
          ...state,
          courierEta:
            findIndex !== -1
              ? [...draftState]
              : [
                  {
                    id: payload.id,
                    eta: payload.eta,
                    timestamp: payload.timestamp,
                  },
                ],
        };
      },
      false,
      'update/ETA'
    );
  },
});

const createOrderEtaSlice: StateCreator<
  IOrderETASlice,
  [['zustand/devtools', never]],
  [],
  IOrderETASlice
> = (set, get) => ({
  orderEta: [],
  setOrderEta: (payload) => set(() => ({ orderEta: payload }), false, 'set/OrderETA'),
  getOrderEtaByUUID: (orderUUID: string) =>
    get().orderEta.find((e: IOrderETA) => e.uuid === orderUUID)?.eta || false,
  updateOrderEta: (payload: IOrderETA) => {
    set(
      (state) => {
        const draftState = Object.assign(state.orderEta, {});
        const findIndex = state.orderEta.findIndex((e) => e.id === payload.id);
        draftState[findIndex] = {
          ...draftState[findIndex],
          eta: payload.eta,
        };
        return {
          ...state,
          orderEta:
            findIndex !== -1
              ? [...draftState]
              : [
                  {
                    id: payload.id,
                    eta: payload.eta,
                    calculatedAt: payload.calculatedAt,
                    uuid: payload.uuid,
                  },
                ],
        };
      },
      false,
      'update/ETA'
    );
  },
});

const createNextRouteSlice: StateCreator<
  NextRoutesSlice,
  [['zustand/devtools', never]],
  [],
  NextRoutesSlice
> = (set, get) => ({
  nextRoutes: [],
  setNextRouteList: (payload) => set(() => ({ nextRoutes: payload }), false, 'set/NEXT'),
  updateNextRoute: (payload) => {
    const isCourierActive = get().nextRoutes.findIndex(
      (r: IRoute) => r.courier.id === payload.courier.id
    );
    set(
      (state) => {
        const draftRouteState = Object.assign(state.nextRoutes, {});
        draftRouteState[isCourierActive] = payload;
        return {
          nextRoutes:
            isCourierActive !== -1
              ? payload.waypoints.length > 0
                ? [...draftRouteState]
                : [...draftRouteState.filter((nr: IRoute) => nr.courier.id !== payload.courier.id)]
              : [...state.nextRoutes, { ...payload }],
        };
      },
      false,
      'update/NEXT'
    );
  },
});

const createServerTime: StateCreator<
  ServerTimeSlice,
  [['zustand/devtools', never]],
  [],
  ServerTimeSlice
> = (set, get) => ({
  serverTime: {
    heartbeat: new Date().toISOString(),
  },
  setServerTime: (data) => {
    const serverTime: IServerTime = { heartbeat: data };
    set(() => ({ serverTime }), false, 'set/SERVER_TIME');
  },
  getServerTime: () => {
    const serverTime = get().serverTime;
    return serverTime ? serverTime.heartbeat : null;
  },
});
export const useBoundDashboardStore = create<
  CourierSlice &
    OrderSlice &
    RoutesSlice &
    CourierStatusList &
    ICourierDeliveryStatus &
    ICourierLocationStatus &
    ICourierETASlice &
    NextRoutesSlice &
    ServerTimeSlice &
    IOrderETASlice
>()(
  devtools(
    subscribeWithSelector((...a) => ({
      ...createCourierSlice(...a),
      ...createOrderSlice(...a),
      ...createRoutesSlice(...a),
      ...createCourierStatusSlice(...a),
      ...createCourierDeliveryStatusSlice(...a),
      ...createCourierLocationSlice(...a),
      ...createCourierEtaSlice(...a),
      ...createOrderEtaSlice(...a),
      ...createNextRouteSlice(...a),
      ...createServerTime(...a),
    })),
    { name: 'Dashboard Global' }
  )
);

export default useBoundDashboardStore;
